import { useState } from 'react';
import axios from 'axios';
import { useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Form } from '@/components/ui/form';
import SpinnerButton from '@/components/common/SpinnerButton';
import Error from '@/components/common/Error';
import Checkbox from '@/components/form/Checkbox';
import { Button } from '@/components/ui/button';

import SignaturePad from '@/app/signature/SignaturePad';

import Waiver from './Waiver';
import WaiverCompletedAlert from './WaiverCompletedAlert';

import { getPresignedUrl } from '@/data/file';
import { usePostUserActivityWaiver } from '@/data/userActivityWaiver';
import { useSelf } from '@/data/user';
import { useActivityObject } from '@/data/activity';

const formSchema = z.object({
  isAgreedToWaiver: z
    .boolean()
    .refine((val) => val === true, 'You must agree to the waiver'),
  signatureUrl: z.string().url('Required')
});

const WaiverForm = ({
  onSuccess,
  onNextStep,
  onBack,
  isComplete,
  activityId
}) => {
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const self = useSelf();
  const activity = useActivityObject(activityId);
  const postUserActivityWaiver = usePostUserActivityWaiver();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      isAgreedToWaiver: false,
      signatureUrl: ''
    }
  });

  const handleSaveSignature = async (signature) => {
    try {
      setApiError('');
      // Convert data URL to Blob
      const response = await fetch(signature);
      const blob = await response.blob();

      // Get a presigned url for the signature
      const presignedUrl = await getPresignedUrl(
        self.data.id,
        `signature-${activityId}-waiver`,
        'image/png',
        'png'
      );

      // Upload the signature to the presigned url
      await axios.put(presignedUrl, blob, {
        headers: {
          'Content-Type': 'image/png'
        }
      });

      const urlObject = new URL(presignedUrl);
      const documentPath = `${urlObject.origin}${urlObject.pathname}`;
      form.setValue('signatureUrl', documentPath);
    } catch (err) {
      setApiError(err.message);
    }
  };

  const onSubmit = async (values) => {
    try {
      setSubmitting(true);
      setApiError('');
      await postUserActivityWaiver(activityId, values);
      onSuccess();
    } catch (err) {
      setApiError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const {
    formState: { errors }
  } = form;

  if (isComplete) {
    return (
      <>
        <WaiverCompletedAlert onSuccess={onNextStep} activity={activity} />
        <Waiver activityId={activityId} />
      </>
    );
  }

  return (
    <>
      <Waiver activityId={activityId} />
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="mb-4">
            <Checkbox
              name="isAgreedToWaiver"
              label="I have read and understand the waiver"
            />
          </div>
          <h3>Signature</h3>
          <SignaturePad onSave={handleSaveSignature} />
          {errors.signatureUrl && (
            <span className="text-destructive">
              {errors.signatureUrl.message}
            </span>
          )}
          {apiError && <Error error={apiError} />}
          <div className="flex justify-between mt-8">
            {onBack ? (
              <Button variant="outline" type="button" onClick={onBack}>
                Back
              </Button>
            ) : (
              <div></div>
            )}
            <SpinnerButton loading={submitting} type="submit">
              Continue
            </SpinnerButton>
          </div>
        </form>
      </Form>
    </>
  );
};

export default WaiverForm;
